import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Save, Check } from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { SEO } from "@/lib/seo";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { useCity } from "@/contexts/CityContext";
import {
  ALERT_CATEGORY_LABELS,
  useAlertSubscriptions,
  useDigestSettings,
  useUpsertDigestSettings,
  useUpsertSubscription,
  type AlertCategory,
} from "@/lib/alerts";
import { cn } from "@/lib/utils";

type DeliveryChannel = "email" | "sms" | "inapp" | "push";

const CATEGORIES: AlertCategory[] = [
  "infectious_disease",
  "immunisation",
  "medication",
  "public_health",
  "recall",
  "clinical_guideline",
  "workforce",
  "other",
];

const CHANNELS: Array<{ value: DeliveryChannel; label: string; help: string }> = [
  { value: "inapp", label: "In-app", help: "Visible in PulseBoard feed" },
  { value: "email", label: "Email", help: "Digest to your practice address" },
  { value: "sms", label: "SMS", help: "Urgent alerts only via Mobile Message" },
];

export default function AlertSubscriptions() {
  const { city, link } = useCity();
  const subs = useAlertSubscriptions(city);
  const digest = useDigestSettings(city);
  const upsertSub = useUpsertSubscription(city);
  const upsertDigest = useUpsertDigestSettings(city);

  const current = subs.data?.items?.[0];
  const [categories, setCategories] = useState<AlertCategory[]>([]);
  const [delivery, setDelivery] = useState<DeliveryChannel[]>(["inapp"]);
  const [dailyDigest, setDailyDigest] = useState(true);
  const [digestTime, setDigestTime] = useState("07:00");
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Sync local state once data arrives.
  useEffect(() => {
    if (current) {
      setCategories(current.categories);
      setDelivery(current.delivery);
      setDailyDigest(current.daily_digest);
    }
  }, [current?.id]);

  useEffect(() => {
    if (digest.data) {
      setDigestTime((digest.data.digest_time_local || "07:00").slice(0, 5));
    }
  }, [digest.data?.digest_time_local]);

  const toggleCategory = (c: AlertCategory) => {
    setCategories((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
    );
  };

  const toggleChannel = (c: DeliveryChannel) => {
    setDelivery((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
    );
  };

  const saveAll = async () => {
    await Promise.all([
      upsertSub.mutateAsync({
        categories,
        delivery,
        daily_digest: dailyDigest,
      }),
      upsertDigest.mutateAsync({
        digest_time_local: digestTime,
      }),
    ]);
    setSavedAt(Date.now());
    setTimeout(() => setSavedAt(null), 3000);
  };

  const isLoading = subs.isLoading || digest.isLoading;
  const isSaving = upsertSub.isPending || upsertDigest.isPending;

  return (
    <PageShell>
      <SEO
        title={`Alert settings · ${city.name} · PulseBoard`}
        description="Choose which public health alerts reach you and when."
        path={`/${city.slug}/alerts/subscriptions`}
        noindex
      />

      <section className="mx-auto max-w-3xl px-4 pb-16 pt-8 md:px-8">
        <Button variant="ghost" size="sm" asChild className="mb-6 -ml-2">
          <Link to={link("/alerts")}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to alerts
          </Link>
        </Button>

        <h1 className="font-display text-4xl font-semibold tracking-tight">
          Alert preferences
        </h1>
        <p className="mt-2 text-pb-ink-2">
          Choose what reaches you, through which channel, at what time.
        </p>

        {isLoading ? (
          <div className="mt-10 space-y-4">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <div className="mt-10 space-y-6">
            {/* Categories */}
            <Card>
              <CardHeader>
                <CardTitle>Categories</CardTitle>
                <CardDescription>
                  Alerts outside these categories won't reach you.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map((c) => {
                    const active = categories.includes(c);
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => toggleCategory(c)}
                        className={cn(
                          "rounded-full border px-4 py-1.5 text-sm font-medium transition",
                          active
                            ? "border-pb-primary bg-pb-primary text-white"
                            : "border-pb-border text-pb-ink-2 hover:border-pb-primary/40",
                        )}
                      >
                        {ALERT_CATEGORY_LABELS[c]}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Channels */}
            <Card>
              <CardHeader>
                <CardTitle>Delivery channels</CardTitle>
                <CardDescription>
                  SMS used only for warning/critical alerts — Mobile Message AU,
                  no Twilio.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {CHANNELS.map((ch) => {
                    const active = delivery.includes(ch.value);
                    return (
                      <button
                        key={ch.value}
                        type="button"
                        onClick={() => toggleChannel(ch.value)}
                        className={cn(
                          "flex w-full items-start gap-3 rounded-lg border px-4 py-3 text-left transition",
                          active
                            ? "border-pb-primary/40 bg-pb-primary/5"
                            : "border-pb-border hover:border-pb-primary/30",
                        )}
                      >
                        <div
                          className={cn(
                            "mt-0.5 flex h-5 w-5 items-center justify-center rounded border",
                            active
                              ? "border-pb-primary bg-pb-primary text-white"
                              : "border-pb-border",
                          )}
                        >
                          {active ? <Check className="h-3.5 w-3.5" /> : null}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-pb-ink">{ch.label}</p>
                          <p className="text-sm text-pb-ink-2">{ch.help}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Digest */}
            <Card>
              <CardHeader>
                <CardTitle>Morning digest</CardTitle>
                <CardDescription>
                  A single summary email at your preferred time each morning.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <label className="flex cursor-pointer items-center justify-between rounded-lg border border-pb-border px-4 py-3">
                  <div>
                    <p className="font-medium text-pb-ink">Daily digest</p>
                    <p className="text-sm text-pb-ink-2">
                      Consolidates the last 24h of alerts into one email.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={dailyDigest}
                    onChange={(e) => setDailyDigest(e.target.checked)}
                    className="h-5 w-5 accent-pb-primary"
                  />
                </label>

                {dailyDigest ? (
                  <div>
                    <Label htmlFor="digest-time" className="text-sm">
                      Digest time (local, {city.timezone})
                    </Label>
                    <input
                      id="digest-time"
                      type="time"
                      value={digestTime}
                      onChange={(e) => setDigestTime(e.target.value)}
                      className="mt-1 w-32 rounded-lg border border-pb-border bg-pb-bg px-3 py-2 text-sm focus:border-pb-primary focus:outline-none focus:ring-1 focus:ring-pb-primary"
                    />
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <div className="flex items-center justify-end gap-3">
              {savedAt ? (
                <span className="flex items-center gap-1 text-sm text-pb-primary">
                  <Check className="h-4 w-4" />
                  Saved
                </span>
              ) : null}
              <Button onClick={saveAll} disabled={isSaving}>
                <Save className="mr-1 h-4 w-4" />
                {isSaving ? "Saving…" : "Save preferences"}
              </Button>
            </div>
          </div>
        )}
      </section>
    </PageShell>
  );
}
